// ─────────────────────────────────────────────────────────────────────────────
// CanonicalGraphSource — the LIVE graph's only topology source (G2.4 · Phase D).
//
// WHAT IT REPLACES. RexyOsStructureGraphSource walked the DTO's legacy arrays and
// rebuilt Rexy OS in the browser: it decided which agents existed, invented
// parentage from naming conventions, and produced the 103-node map that could not
// see the eleven-strong CODE build team and showed DOCTOR as a live agent. This
// class calculates NOTHING. It fetches, hands the payload to
// canonicalTopologyAdapter, and serves what comes back.
//
// ONE TRUTH, AND IT IS NOT HERE. There is no domain list, no agent list, no team
// list and no memory list in this file. Search is a scan of the store the adapter
// produced, not a second index. If an entity is missing from the galaxy the fix
// is in the backend snapshot, never a patch here.
//
// READ-ONLY. Two GETs exist in the whole live path — the topology and its
// revision — and both are authenticated, GET-only reads. Nothing here writes,
// invokes an agent, runs a model, reads a memory body or decrypts anything.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  RexyGraphNodeRecord,
  RexyGraphPatch,
  RexyGraphSnapshot,
} from './graphTypes';
import type { RexyGraphSource } from './graphSource';
import {
  buildCanonicalSnapshot,
  deriveLiveCounts,
  diffSnapshots,
  type AdapterResult,
  type CanonicalSnapshotDto,
} from './canonicalTopologyAdapter';

export type CanonicalCounts = ReturnType<typeof deriveLiveCounts>;

/** Shape the dev dock already renders: what, and how serious. */
export interface CanonicalParityIssue {
  severity: 'info' | 'drift' | 'ambiguity';
  entity: string;
  issue: string;
}

export interface CanonicalSourceStatus {
  /** The revision the store currently reflects. */
  revision: string;
  /** Wall-clock of the last SUCCESSFUL topology load. */
  lastGoodAt: number | null;
  /** True once a refresh has failed while a good snapshot is still on screen. */
  stale: boolean;
  /** Last failure message, for the dev panel and the stale banner. */
  error: string | null;
}

export interface CanonicalSourceOptions {
  fetchStructure: () => Promise<any>;
  fetchRevision: () => Promise<{ revision: string; refreshedAt?: string | null;
    checkedAt?: string | null; }>;
  /** Poll interval. Defaults to the backend's own read-through TTL. */
  pollMs?: number;
  onStatus?: (s: CanonicalSourceStatus) => void;
  onCounts?: (c: CanonicalCounts) => void;
}

/** The backend cache refreshes on a 15s read-through TTL; polling faster only
 *  costs requests without seeing anything sooner. */
export const DEFAULT_POLL_MS = 15000;

export class CanonicalGraphSource implements RexyGraphSource {
  private opts: CanonicalSourceOptions;
  private result: AdapterResult | null = null;
  private dto: CanonicalSnapshotDto | null = null;
  private snapshot: RexyGraphSnapshot | null = null;
  private timer: any = null;
  private subscribers: Array<(p: RexyGraphPatch) => void> = [];
  private status: CanonicalSourceStatus = {
    revision: '—', lastGoodAt: null, stale: false, error: null,
  };

  constructor(opts: CanonicalSourceOptions) {
    this.opts = opts;
  }

  getStatus(): CanonicalSourceStatus {
    return { ...this.status };
  }

  getCounts(): CanonicalCounts | null {
    return this.result ? deriveLiveCounts(this.result) : null;
  }

  /** Every node currently in the live store — the ONE index the tree, search
   *  and Inspector all read. */
  getNodes(): RexyGraphNodeRecord[] {
    return this.snapshot ? this.snapshot.nodes : [];
  }

  getSnapshot(): RexyGraphSnapshot | null {
    return this.snapshot;
  }

  /** Dev/parity view: the same canonical payload including the entities held
   *  out of the live galaxy (declared-not-built, orphans). */
  getDiagnosticEntities() {
    return this.result ? this.result.allEntities : [];
  }

  getAdapterReport() {
    return this.result ? this.result.report : null;
  }

  private applyStatus(patch: Partial<CanonicalSourceStatus>) {
    this.status = { ...this.status, ...patch };
    if (this.opts.onStatus) this.opts.onStatus(this.getStatus());
  }

  private ingest(dto: CanonicalSnapshotDto): RexyGraphSnapshot {
    const next = buildCanonicalSnapshot(dto);
    this.dto = dto;
    this.result = next;
    this.snapshot = next.snapshot;
    this.applyStatus({
      revision: String(dto.revision), lastGoodAt: Date.now(),
      stale: false, error: null,
    });
    if (this.opts.onCounts) this.opts.onCounts(deriveLiveCounts(next));
    return next.snapshot;
  }

  async loadRoot(): Promise<RexyGraphSnapshot> {
    // A first-load failure must propagate: the caller shows LIVE REXY STRUCTURE
    // UNAVAILABLE. Returning an empty snapshot here would render an OS with no
    // domains in it, which looks like a fact rather than a fault.
    const dto = await this.opts.fetchStructure();
    return this.ingest(dto);
  }

  /**
   * The canonical snapshot is complete on first load — every entity arrives at
   * once — so there is nothing to hydrate later. Returning an empty patch is the
   * honest answer, and it keeps the source interface satisfied without inventing
   * a lazy-loading protocol the backend does not have.
   */
  async loadChildren(_parentId: string, _depth: number): Promise<RexyGraphPatch> {
    return { revision: this.status.revision, upsertNodes: [], upsertEdges: [] };
  }

  /**
   * Search scans the live store. It matches label and canonical kind only —
   * never memory contents, which the graph does not have and must not fetch.
   */
  async search(query: string, limit = 40): Promise<RexyGraphNodeRecord[]> {
    const q = query.trim().toLowerCase();
    if (!q || !this.snapshot) return [];
    const out: RexyGraphNodeRecord[] = [];
    for (const n of this.snapshot.nodes) {
      const meta = (n.metadata || {}) as any;
      const hay = [n.label, String(meta.canonicalKind || n.kind),
        String(meta.domainKey || '')].join(' ').toLowerCase();
      if (hay.indexOf(q) !== -1) out.push(n);
      if (out.length >= limit) break;
    }
    return out;
  }

  subscribe(callback: (patch: RexyGraphPatch) => void): () => void {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter((c) => c !== callback);
    };
  }

  /**
   * One poll tick: ask for the revision, and fetch topology ONLY if it moved.
   *
   * A failed refresh keeps the last-known-good snapshot on screen, marks it
   * STALE and leaves `revision` where it was, so the next tick retries the same
   * comparison rather than adopting a revision whose topology never arrived.
   */
  async poll(): Promise<'unchanged' | 'patched' | 'failed'> {
    try {
      const rev = await this.opts.fetchRevision();
      if (String(rev.revision) === this.status.revision) return 'unchanged';

      const dto = await this.opts.fetchStructure();
      const before = this.snapshot;
      const after = this.ingest(dto);
      const patch = diffSnapshots(before, after);
      for (const cb of this.subscribers) cb(patch);
      return 'patched';
    } catch (err: any) {
      this.applyStatus({
        stale: this.snapshot !== null,
        error: err && err.message ? String(err.message) : 'live refresh failed',
      });
      return 'failed';
    }
  }

  /**
   * Canonical findings, surfaced to the dev dock in the shape it already reads.
   *
   * These are the BACKEND's drift findings — declared-not-built agents, the
   * registry blind spot, unmaterialised collections, absent boards — plus any
   * kind this adapter could not classify. The frontend discovers no drift of its
   * own; it reports what the canonical snapshot found.
   */
  getParity(): CanonicalParityIssue[] {
    const out: CanonicalParityIssue[] = [];
    const report = this.result?.report;
    if (report) {
      for (const k of Array.from(new Set(report.unmappedKinds))) {
        out.push({ severity: 'ambiguity', entity: k,
          issue: 'canonical kind has no frontend visual mapping' });
      }
      for (const e of report.excludedFromLive) {
        out.push({ severity: 'info', entity: e.id,
          issue: `held out of the live galaxy (${e.state})` });
      }
    }
    // The backend's own drift findings — the authoritative ones. The frontend
    // discovers no drift of its own; it reports what canonical discovery found.
    for (const d of (this.dto?.drift || [])) {
      out.push({ severity: 'drift', entity: d.entity, issue: d.finding });
    }
    return out;
  }

  /**
   * A manual refresh — the dock's button and the parity harness both use it.
   * Shares the poll path so there is one refresh implementation, not two.
   */
  async refresh(): Promise<{ changed: boolean; revision: string; patch: RexyGraphPatch | null }> {
    let captured: RexyGraphPatch | null = null;
    const un = this.subscribe((p) => { captured = p; });
    try {
      const outcome = await this.poll();
      if (outcome === 'failed') throw new Error(this.status.error || 'live refresh failed');
      return { changed: outcome === 'patched', revision: this.status.revision, patch: captured };
    } finally {
      un();
    }
  }

  /** Start polling. Idempotent — a second call never creates a second timer. */
  startPolling() {
    if (this.timer !== null) return;
    const every = this.opts.pollMs || DEFAULT_POLL_MS;
    this.timer = setInterval(() => { void this.poll(); }, every);
  }

  stopPolling() {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}
